import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { fetchConfig } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * "Get an email when it's back" on a sold-out size (blocks/restock-alert.liquid).
 *
 * Submits to the Cornelius app's App Proxy, which forwards to the hub with a
 * signed query naming the signed-in customer, so a signed-in customer sends
 * no email of their own. Listens on the host element so the handler survives
 * the morph that re-renders the block when the size changes.
 */
class RestockAlertForm extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('submit', this.#handleSubmit);
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('submit', this.#handleSubmit);
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Re-renders from the section HTML the variant picker fetched, as the
   * Inventory block does.
   * @param {Event & { promise?: Promise<any> }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;
        const { html, newProduct } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }
        const next = html.querySelector('restock-alert-form');
        if (!next) return;
        this.#activeFetch?.abort();
        morph(this, next, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[restock-alert] Event promise rejected:', error);
      });
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('restock-alert__form')) return;
    event.preventDefault();

    const input = form.querySelector('input[name="email"]');
    const button = form.querySelector('button[type="submit"]');
    const email = input instanceof HTMLInputElement ? input.value.trim() : '';

    if (input instanceof HTMLInputElement && !input.checkValidity()) {
      input.setAttribute('aria-invalid', 'true');
      this.#showStatus(form, input.validationMessage, true);
      input.focus();
      return;
    }

    input?.removeAttribute('aria-invalid');
    this.#showStatus(form, '', false);
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    button?.setAttribute('aria-busy', 'true');

    try {
      const body = { variantId: form.dataset.variantId, ...(email ? { email } : {}) };
      const response = await fetch(form.action, {
        ...fetchConfig('json', { body: JSON.stringify(body) }),
        signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.status === 'available') {
        this.#showStatus(form, this.dataset.available || '', false);
        return;
      }
      if (response.ok) {
        this.#showSuccess(form, email);
        return;
      }
      // A 400 or 429 carries a sentence meant for the shopper; anything else
      // gets the block's own retry message.
      const message =
        (response.status === 400 || response.status === 429) && typeof data.error === 'string' ? data.error : this.dataset.error || '';
      this.#showStatus(form, message, true);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.warn('[restock-alert]', error);
      this.#showStatus(form, this.dataset.error || '', true);
    } finally {
      button?.removeAttribute('aria-busy');
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  };

  /**
   * Swaps the form for the confirmation the block rendered hidden, filling in
   * a guest's address (a signed-in customer's is already there), and moves
   * focus to it, since the submit button that held focus is now hidden.
   * @param {HTMLFormElement} form
   * @param {string} email
   */
  #showSuccess(form, email) {
    const content = form.closest('.restock-alert__content');
    const success = this.querySelector('.restock-alert__success');
    if (!(content instanceof HTMLElement) || !(success instanceof HTMLElement)) return;
    if (email) {
      for (const node of success.querySelectorAll('[data-restock-email]')) node.textContent = email;
    }
    content.hidden = true;
    success.hidden = false;
    success.focus({ preventScroll: true });
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} text
   * @param {boolean} isError
   */
  #showStatus(form, text, isError) {
    const status = form.querySelector('.restock-alert__status');
    const statusText = status?.querySelector('.restock-alert__status-text');
    if (!(status instanceof HTMLElement) || !statusText) return;
    statusText.textContent = text;
    status.hidden = !text;
    status.classList.toggle('restock-alert__status--error', isError);
  }
}

if (!customElements.get('restock-alert-form')) {
  customElements.define('restock-alert-form', RestockAlertForm);
}
